import React, { useState } from 'react';
import classNames from 'classnames';

import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';
import SearchableFilterOptions from '../SearchableFilterOptions/SearchableFilterOptions';

import css from './SelectMultipleFilter.module.css';

// Filters like "league" or "club" can have hundreds/thousands of options.
// Rendering every one of them as a checkbox the instant the popup opens is
// what made opening/closing these dropdowns freeze the page - so cap what
// renders up front and let the search box (which still searches the full
// option list) narrow it down instead.
const MAX_VISIBLE_OPTIONS_WITHOUT_SEARCH = 100;

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, searchTerm = '' } = props;
  // Filter out 'for_sale' and 'not_for_sale' options
  const filteredOptions = options.filter(
    optionConfig => optionConfig.option !== 'for_sale' && optionConfig.option !== 'not_for_sale'
  );

  // Filter by search term if provided
  const searchFilteredOptions = searchTerm
    ? filteredOptions.filter(optionConfig =>
      optionConfig.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : filteredOptions;

  const visibleOptions =
    !searchTerm && searchFilteredOptions.length > MAX_VISIBLE_OPTIONS_WITHOUT_SEARCH
      ? searchFilteredOptions.slice(0, MAX_VISIBLE_OPTIONS_WITHOUT_SEARCH)
      : searchFilteredOptions;
  const hiddenOptionsCount = searchFilteredOptions.length - visibleOptions.length;

  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {visibleOptions.length > 0 ? (
          visibleOptions.map(optionConfig => {
            const { option, label, colorHex } = optionConfig;
            const fieldId = `${id}.${option}`;

            // Create label with color circle if colorHex is provided
            const labelContent = colorHex ? (
              <span className={css.labelWithColor}>
                <span className={css.colorCircle} style={{ backgroundColor: colorHex }} />
                {label}
              </span>
            ) : label;

            return (
              <li key={fieldId} className={classNames(css.item, {
                [css.topSpecialItem]: label === 'Nationalmannschaft' || label === 'Kein Flock',
              })}>
                <FieldCheckbox id={fieldId} name={name} label={labelContent} value={option} />
              </li>
            );
          })
        ) : (
          <li className={css.noResults}>No options found</li>
        )}
        {hiddenOptionsCount > 0 ? (
          <li className={css.noResults}>
            +{hiddenOptionsCount} more - type to search
          </li>
        ) : null}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    isUserFilter,
    ...rest
  } = props;

  const [searchTerm, setSearchTerm] = useState('');

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const parsedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  // When multiple filters share the same query param (e.g. 'keywords'),
  // ensure that selection state is computed only from values that belong
  // to this filter's options. This prevents unrelated filters from
  // appearing selected just because the shared query param has values.
  // Id-only match: checkbox values are always option ids (see
  // GroupOfFieldCheckboxes above), and matching by label too would make a
  // plain typed search word that happens to equal an option's label (e.g.
  // "Suarez") show up as a pre-checked filter it never selected.
  const optionValuesSet = new Set((options || []).map(o => `${o.option}`));

  const selectedOptions = parsedOptions.filter(p => optionValuesSet.has(`${p}`));

  // hasInitialValuesForThisFilter should reflect whether this particular
  // filter has any selected options (not just whether the shared param exists)
  const hasInitialValuesForThisFilter = selectedOptions && selectedOptions.length > 0;

  // Create label for popup opener that contains the main label and a separate
  // span for the selected count so it can be styled independently.
  const labelForPopupElement = hasInitialValuesForThisFilter ? (
    <span className={css.popupLabel}>
      <span className={css.popupLabelText}>{label}</span>
      <span className={css.selectedCount} aria-hidden>
        {selectedOptions.length}
      </span>
    </span>
  ) : (
    label
  );

  // For plain form, provide the count as a separate element; FilterPlain will
  // render it inside its labelSelected span, so we only need to supply the
  // inner element representing the count.
  const labelSelectionForPlain = hasInitialValuesForThisFilter ? (
    <span className={css.selectedCountPlain} aria-hidden>
      {selectedOptions.length}
    </span>
  ) : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  // Define which filters should have the searchable options feature
  const searchableFilters = ['country', 'league', 'club', 'player', 'season', 'brand'];
  const shouldShowSearch = searchableFilters.includes(name);

  const filterContent = shouldShowSearch ? (
    <SearchableFilterOptions
      onSearch={setSearchTerm}
      placeholder="Search options..."
      searchTerm={searchTerm}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        searchTerm={searchTerm}
      />
    </SearchableFilterOptions>
  ) : (
    <GroupOfFieldCheckboxes
      className={css.fieldGroup}
      name={name}
      id={`${id}-checkbox-group`}
      options={options}
    />
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopupElement}
      // When popup is open, show the same label (with count) so counts are
      // visible also while the dropdown is open.
      labelWhenOpen={labelForPopupElement}
      isSelected={hasInitialValuesForThisFilter}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      liveEdit
      onChange={handleSubmit}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      isUserFilter
      {...rest}
    >
      {filterContent}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValuesForThisFilter}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
